import { ArrowUpRight, Plus } from 'lucide-react';
import type { ReactElement } from 'react';
import { Link } from 'react-router';

import { PORTFOLIO } from '@/entities/portfolio';
import { cn } from '@/shared/lib/utils';
import { Highlight } from '@/shared/ui/highlight';

import { SLIDE_FRAME, SlideEyebrow, SlideNumber } from './slide-parts';

/**
 * 카피 원칙 — 히어로가 「우리 회사 업무로 배우고 바로 쓴다」와 「끝나면 자동화가 남는다」를
 * 약속한다(education-hero). 아래 모든 섹션은 그 약속을 갚는 방식으로 쓴다.
 *
 * 1. 결과를 **시점과 함께** 말한다 ("끝나는 날", "4주 뒤")
 * 2. 범주가 아니라 **장면**을 쓴다 ("반복 업무가 많다" ✗ → "월요일마다 같은 보고서를" ✓)
 * 3. 각 프로그램은 **무엇이 남는지**를 명시한다. 남는 게 없으면 그건 교육이 아니라 강연이다
 */
const FAQS = [
  {
    q: '비전공자도 따라갈 수 있나요?',
    a: '코드를 작성하지 않습니다. 이미 있는 도구를 조합하는 방식으로 진행합니다.',
  },
  {
    q: '어떤 도구를 다루나요?',
    a: 'ChatGPT, Claude, Gemini, Genspark, Grok 을 다룹니다. 도구별 사용법에 앞서 LLM과 디퓨전의 동작 원리를 먼저 설명하고, 그 위에서 활용법을 다룹니다. 필요하신 방식에 맞춰 구성할 수 있으니 편하게 문의해 주세요.',
  },
  {
    q: '최소 인원이 있나요?',
    a: '정해진 하한은 없습니다. 상황에 맞게 조율할 수 있습니다.',
  },
  {
    q: '온라인으로도 되나요?',
    a: '가능합니다. 실습 비중이 커서 첫 회차는 대면으로 권합니다.',
  },
  {
    q: '기간과 비용은 어떻게 되나요?',
    a: '업무 범위와 인원에 따라 다릅니다. 문의 주시면 진단 후 구성과 함께 안내드립니다.',
  },
] as const;

const SLIDE_HEADING =
  'text-[clamp(2rem,1.3rem+2.4vw,3.25rem)] font-bold leading-[1.22] tracking-[-0.05em]';

const SLIDE_LEAD = 'max-w-[32rem] text-[16px] leading-[1.85] text-muted-foreground lg:text-[17px]';

/**
 * 사례 — 서비스 슬라이드와 같은 틀(왼쪽 문장 · 오른쪽 큰 그림)로 한 장.
 *
 * 카드는 훑는 물건이다. 라벨을 여러 개 붙이면 보고서가 되어 아무도 안 읽는다 —
 * **결과 한 줄만 크게** 남기고 나머지는 상세에서 말한다. 한 프로젝트를 여러 카드로
 * 쪼개지 않는다(쪼개면 실적 부풀리기로 읽힌다). 사례가 늘면 오른쪽 목록이 길어진다.
 */
export function EducationPortfolio(): ReactElement {
  return (
    <section id="edu-portfolio" aria-labelledby="edu-portfolio-heading" className={SLIDE_FRAME}>
      <SlideNumber>04</SlideNumber>
      <div className="u-container grid items-center gap-12 py-20 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] lg:gap-20 lg:py-16">
        <div className="flex flex-col items-start gap-7">
          <SlideEyebrow>Case · 교육·프로젝트 사례</SlideEyebrow>
          <h2 id="edu-portfolio-heading" className={SLIDE_HEADING}>
            교육·프로젝트 <Highlight>사례</Highlight>
          </h2>
          <p className={SLIDE_LEAD}>
            기업과 기관에서 진행한 사례입니다. 교육 중에 만든 결과물까지 함께 보여드립니다.
          </p>
        </div>

        <ul className="grid gap-5">
          {PORTFOLIO.map((item) => (
            <li key={item.id}>
              <Link
                to={`/education/portfolio/${item.id}`}
                className={cn(
                  'group flex flex-col overflow-hidden rounded-[36px] border border-border bg-card transition duration-300',
                  'hover:-translate-y-1 hover:border-foreground hover:shadow-[0_30px_60px_-36px_rgb(6_17_11/0.45)]',
                  'focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring',
                )}
              >
                <div className="relative aspect-[16/9] overflow-hidden bg-secondary">
                  {/* 로고는 꽉 채우지 않는다 — 잘리고, 브랜드 규정에도 어긋난다 */}
                  <img
                    src={item.thumb}
                    alt={item.thumbAlt}
                    loading="lazy"
                    className={cn(
                      'absolute inset-0 size-full transition-transform duration-500 group-hover:scale-[1.03]',
                      item.thumbKind === 'logo'
                        ? 'bg-card object-contain p-12 sm:p-16'
                        : 'object-cover',
                    )}
                  />
                  <span className="absolute top-5 left-5 rounded-full bg-ink-surface px-3 py-1 text-[12px] font-medium text-on-ink">
                    {item.badge}
                  </span>
                </div>

                <div className="flex items-end justify-between gap-6 p-7">
                  <div className="flex flex-col gap-2">
                    <p className="text-[13px] text-muted-foreground">{item.title}</p>
                    <p className="text-[clamp(1.125rem,0.95rem+0.6vw,1.375rem)] font-bold leading-[1.45] tracking-[-0.035em]">
                      {item.outcome}
                    </p>
                    <p className="text-[12.5px] text-muted-foreground">{item.caption}</p>
                  </div>
                  <span
                    aria-hidden="true"
                    className="grid size-12 shrink-0 place-items-center rounded-full bg-primary text-on-mint transition-transform duration-300 group-hover:rotate-45"
                  >
                    <ArrowUpRight className="size-5" strokeWidth={2} />
                  </span>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/**
 * FAQ — 제목은 왼쪽, 질문은 오른쪽. 슬라이드 한 장에 다섯 개가 다 들어온다.
 *
 * details/summary 를 그대로 쓴다. 여는 동작에 자바스크립트가 필요 없고,
 * 키보드와 화면 낭독기가 이미 아는 요소라 따로 붙일 것이 없다.
 * name 을 같이 줘 한 번에 하나만 열리게 한다 — 여러 개가 열리면 슬라이드 밖으로 넘친다.
 */
export function EducationFaq(): ReactElement {
  return (
    <section aria-labelledby="edu-faq-heading" className={SLIDE_FRAME}>
      <SlideNumber>06</SlideNumber>
      <div className="u-container grid gap-12 py-20 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] lg:items-center lg:gap-20 lg:py-16">
        <div className="flex flex-col items-start gap-7">
          <SlideEyebrow>FAQ · 자주 묻는 질문</SlideEyebrow>
          <h2 id="edu-faq-heading" className={SLIDE_HEADING}>
            자주 묻는 <Highlight>질문</Highlight>
          </h2>
          <p className={SLIDE_LEAD}>여기 없는 궁금한 점은 문의로 편하게 남겨 주세요.</p>
        </div>

        <div className="border-foreground border-t">
          {FAQS.map((faq) => (
            <details key={faq.q} name="edu-faq" className="group border-border border-b">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-6 py-6 text-[17px] font-semibold tracking-[-0.03em] [&::-webkit-details-marker]:hidden">
                {faq.q}
                <span
                  aria-hidden="true"
                  className="grid size-10 shrink-0 place-items-center rounded-full border border-border transition duration-300 group-open:rotate-45 group-open:border-transparent group-open:bg-primary group-open:text-on-mint"
                >
                  <Plus className="size-4" strokeWidth={2} />
                </span>
              </summary>
              <p className="max-w-[44rem] pb-7 text-[15px] leading-[1.85] text-muted-foreground">
                {faq.a}
              </p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * 랜딩 끝.
 *
 * **가운데 정렬 세로 스택을 쓰지 않는다.** 어느 랜딩에나 있는 모양이라 눈에 남지 않고,
 * 제목·설명·버튼·보조문구를 쌓으면 마지막 순간에 읽을 것이 넷이 된다.
 *
 * 좌우로 나눈다 — 시선이 왼쪽 문구를 지나 **오른쪽 버튼에서 멈춘다.**
 * 위에서 이미 다 설명했으므로 여기 문구는 두 줄을 넘기지 않는다.
 *
 * 민트를 면으로 깔았다. 위 섹션들이 흰색·잉크·회색으로 왔으므로 민트는 여기가 처음이다.
 * 글자로는 못 쓰지만(흰 배경 1.29:1) 면으로 깔면 잉크 글자가 15.48:1 로 읽힌다.
 */
export function EducationCta(): ReactElement {
  return (
    <section aria-labelledby="edu-cta-heading" className="bg-primary text-primary-foreground">
      <div className="u-container flex flex-col gap-8 py-16 md:flex-row md:items-center md:justify-between md:gap-12 md:py-20">
        <div className="flex flex-col gap-3">
          <h2
            id="edu-cta-heading"
            className="text-[clamp(1.5rem,1.2rem+1.4vw,2.25rem)] font-bold leading-[1.3] tracking-[-0.045em]"
          >
            어떤 업무부터 줄일 수 있을지
            <br className="hidden sm:block" /> 같이 봅니다
          </h2>
          <p className="text-[14.5px] leading-[1.75] text-primary-foreground/75">
            정해서 오실 필요 없습니다. 그것부터 함께 고릅니다.
          </p>
        </div>

        <div className="flex shrink-0 flex-col items-start gap-2 md:items-end">
          <Link
            to="/education/inquiry"
            className="inline-flex h-14 items-center rounded-md bg-ink-surface px-9 text-[16px] font-semibold text-on-ink transition-opacity hover:opacity-90"
          >
            교육 문의하기
          </Link>
          <span className="text-[12.5px] text-primary-foreground/70">
            영업일 기준 2일 안에 답변드립니다
          </span>
        </div>
      </div>
    </section>
  );
}
